import React from 'react';

interface VenereButtonProps {
  children?: React.ReactNode;
  variant?: 'primary' | 'secondary-royal' | 'secondary-lavender' | 'action' | 'back' | 'close' | 'forward';
  size?: 'sm' | 'md' | 'lg';
  loading?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  type?: 'button' | 'submit' | 'reset';
}

export const VenereButton: React.FC<VenereButtonProps> = ({
  children,
  variant = 'primary',
  size = 'md',
  loading = false,
  disabled = false,
  onClick,
  className = '',
  type = 'button',
}) => {
  const textRef = React.useRef<HTMLSpanElement>(null);
  const [textWidth, setTextWidth] = React.useState<number>(0);

  React.useEffect(() => {
    if (textRef.current) {
      setTextWidth(textRef.current.offsetWidth);
    }
  }, [children]);
  const baseClasses = 'inline-flex items-center justify-center rounded-[6px] text-[12px] font-bold py-[10px] px-[15px] transition-all duration-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed min-w-[80px] hover:opacity-80 focus:opacity-65';
  
  const variantClasses = {
    primary: `venere-bg-navy venere-text-skin`,
    'secondary-royal': `venere-bg-royal venere-text-matcha`,
    'secondary-lavender': `venere-bg-lavender venere-text-navy`,
    action: `venere-bg-seasalt venere-text-navy hover:opacity-100 venere-hover-navy`,
    back: `bg-white venere-text-navy border border-solid`,
    close: `bg-white venere-text-navy border border-solid`,
    forward: `bg-white venere-text-navy border border-solid`,
  };

  const sizeClasses = {
    sm: 'py-[6px] px-[10px] text-[10px]',
    md: 'py-[10px] px-[15px] text-[12px]', // Default size
    lg: 'py-[14px] px-[20px] text-[14px]',
  };

  const isDisabled = disabled || loading;

  return (
    <button
      type={type}
      style={{ minWidth: textWidth > 0 ? `${textWidth + 32}px` : 'auto' }}
      className={`${baseClasses} ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
      onClick={onClick}
      disabled={isDisabled}
    >
      {loading ? (
        <div className="flex items-center">
          <div className="animate-spin rounded-full h-3 w-3 border-2 border-solid border-t-transparent"></div>
        </div>
      ) : (
        <div className="flex items-center justify-center w-full">
          {variant === 'back' && (
            <svg 
              className="w-3 h-3 flex-shrink-0" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M15 19l-7-7 7-7" 
              />
            </svg>
          )}
          {variant === 'forward' && (
            <svg
              className="w-3 h-3 flex-shrink-0"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M9 5l7 7-7 7"
              />
            </svg>
          )}
          {variant !== 'back' && variant !== 'close' && (
            <span ref={textRef}>{children}</span>
          )}
          {variant === 'action' && (
            <svg 
              className="w-3 h-3 ml-2 flex-shrink-0" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M9 5l7 7-7 7" 
              />
            </svg>
          )}
          {variant === 'close' && (
            <svg 
              className="w-3 h-3 flex-shrink-0" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path 
                strokeLinecap="round" 
                strokeLinejoin="round" 
                strokeWidth={2} 
                d="M6 18L18 6M6 6l12 12" 
              />
            </svg>
          )}
        </div>
      )}
    </button>
  );
};

export default VenereButton; 